import { useLoadingStore } from '@caradasstore/LoadingStore'
import { AdasBox, AdasPagination, AdasPaper, AdasStack } from '@components/wrapper-components'
import { ChevronDown, ChevronUp, Download, Globe, Pencil, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ItemPerPageSelector } from '@components/common'
import { ClientInfoModal } from '@components/client'
import { getAllClients, getCalibrationCenterList } from '../../api/api'
import { ccidFormatter, downloadAsFile, roundToGreatestInteger } from '../../utils/utils'
import { Logger } from '../../logger'
import { scrollPageTop } from '../../utils/useScrollTop'

// "All centers" sentinel for the filter dropdown — distinct from any real ccid so the
// API request can simply omit `ccid` when this is selected.
const ALL_CENTERS = '__ALL__'

// Frontend column id → backend `sortBy` field. Status is not server-sortable per the
// API contract, so we exclude it from the sortable columns.
const SORT_FIELD_MAP = {
  name: 'name',
  token: 'ccc_client_id',
  centerName: 'calibration_center_name',
  centerCcid: 'calibration_center_id'
}

export const GlobalClientsPage = () => {
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [rows, setRows] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [centers, setCenters] = useState([]) // [{ id, name }] — populates the filter dropdown.
  const [centerFilter, setCenterFilter] = useState(ALL_CENTERS)
  const [isActive, setIsActive] = useState(true) // 'All' tab unsets this; 'Active' tab is the default.
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState('ASC')
  const [sortField, setSortField] = useState('name')
  const [pageNo, setPageNo] = useState(0) // 0-indexed per the API contract.
  const [pageSize, setPageSize] = useState(50)
  const [centerDropdownOpen, setCenterDropdownOpen] = useState(false)
  const [editClient, setEditClient] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  const initialMount = useRef(true)

  // Fetch the center list once so the filter dropdown can render their names. We still
  // use the existing `getCalibrationCenterList` because /v1/clients/all returns the
  // names per row but not the full center catalog needed for the dropdown.
  useEffect(() => {
    let cancelled = false
    const fetchCenters = async () => {
      try {
        const probe = await getCalibrationCenterList({ pageNo: 0, pageSize: 500 })
        if (probe?.status !== 200) return
        const total = probe.data.totalCount || 0
        let centerList = probe.data.calibrationCenters || []
        if (total > centerList.length) {
          const full = await getCalibrationCenterList({ pageNo: 0, pageSize: total })
          if (full?.status === 200) centerList = full.data.calibrationCenters || []
        }
        if (cancelled) return
        setCenters(
          centerList
            .filter((cc) => cc?.id != null && cc.is_active !== false)
            .map((cc) => ({ id: cc.id, name: cc.name || '' }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' }))
        )
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'GlobalClientsPage', method: 'fetchCenters' } })
      }
    }
    fetchCenters()
    return () => {
      cancelled = true
    }
  }, [])

  // Debounce the search input so each keystroke doesn't fire a request.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400)
    return () => clearTimeout(id)
  }, [searchInput])

  // Reset to page 0 whenever any filter / sort key changes so the user doesn't get
  // stranded on an empty page after narrowing the result set.
  useEffect(() => {
    if (initialMount.current) return
    setPageNo(0)
  }, [centerFilter, debouncedSearch, sort, sortField, pageSize, isActive])

  // Main fetch effect — re-runs on any query-state change.
  useEffect(() => {
    let cancelled = false
    const sortBy = SORT_FIELD_MAP[sortField] || 'name'

    const fetchClients = async () => {
      setLoading(true)
      try {
        const response = await getAllClients({
          ccid: centerFilter === ALL_CENTERS ? undefined : centerFilter,
          pageNo,
          pageSize,
          sort,
          sortBy,
          search: debouncedSearch,
          isActive: isActive === true ? 'true' : undefined
        })
        if (cancelled || response?.status !== 200) return
        const data = response.data || {}
        const dtos = Array.isArray(data.clientDtos) ? data.clientDtos : []
        // Preserve the full DTO on each row so the edit modal can hydrate ClientForm
        // directly. The derived display fields are spread on top of the DTO.
        setRows(
          dtos.map((c) => ({
            ...c,
            is_active: c.is_active ?? c.active ?? true,
            token: c.ccc_client_id || '',
            centerId: c.calibration_center_id,
            centerName: c.calibration_center_name || '',
            centerCcid: ccidFormatter(c.calibration_center_id)
          }))
        )
        setTotalCount(data.totalCount || 0)
        setTotalPages(data.totalPages || 0)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'GlobalClientsPage', method: 'getAllClients' } })
      } finally {
        if (!cancelled) {
          setLoading(false)
          initialMount.current = false
        }
      }
    }

    fetchClients()
    return () => {
      cancelled = true
    }
  }, [centerFilter, debouncedSearch, pageNo, pageSize, sort, sortField, isActive, refreshTick, setLoading])

  const clickSortHandler = useCallback(
    (field) => {
      if (!SORT_FIELD_MAP[field]) return
      if (sortField === field) {
        setSort((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))
      } else {
        setSortField(field)
        setSort('ASC')
      }
    },
    [sortField]
  )

  // Active/Inactive counts on the current page only — the API doesn't break the total
  // out by status, so this footer reflects what is currently rendered.
  const activeShown = useMemo(() => rows.filter((r) => r.is_active).length, [rows])
  const inactiveShown = rows.length - activeShown

  const selectedCenterLabel = useMemo(() => {
    if (centerFilter === ALL_CENTERS) return 'All calibration centers'
    return centers.find((c) => c.id === centerFilter)?.name || 'All calibration centers'
  }, [centerFilter, centers])

  // CSV exports the full filtered set — uses pageSize=totalCount on a one-shot call so
  // the user gets every row matching their filter, not just the current page.
  const downloadCSV = useCallback(async () => {
    try {
      const sortBy = SORT_FIELD_MAP[sortField] || 'name'
      const exportResponse = await getAllClients({
        ccid: centerFilter === ALL_CENTERS ? undefined : centerFilter,
        pageNo: 0,
        pageSize: Math.max(totalCount, 1),
        sort,
        sortBy,
        search: debouncedSearch,
        isActive: isActive === true ? 'true' : undefined
      })
      const exportRows = exportResponse?.status === 200 ? exportResponse.data?.clientDtos || [] : []
      const header = ['Client', 'CCC ID', 'Calibration Center', 'CC #', 'Status']
      const csvRows = exportRows.map((c) => [
        `"${c.name || ''}"`,
        c.ccc_client_id || '',
        `"${c.calibration_center_name || ''}"`,
        ccidFormatter(c.calibration_center_id),
        c.is_active ? 'Active' : 'Inactive'
      ])
      const csv = [header, ...csvRows].map((row) => row.join(',') + '\n')
      downloadAsFile(new Blob(csv, { type: 'text/plain' }), 'global_clients.csv')
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'GlobalClientsPage', method: 'downloadCSV' } })
    }
  }, [centerFilter, debouncedSearch, sort, sortField, totalCount, isActive])

  const columns = [
    { id: 'name', label: 'Client', sortable: true, align: 'left' },
    { id: 'token', label: 'CCC ID', sortable: true, align: 'left' },
    { id: 'centerName', label: 'Calibration Center', sortable: true, align: 'left' },
    { id: 'centerCcid', label: 'CC #', sortable: true, align: 'left' },
    { id: 'is_active', label: 'Status', sortable: false, align: 'center' }
  ]

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0'>
      {/* Heading + Export CSV */}
      <AdasBox className='flex flex-wrap items-end justify-between gap-3 pt-6 md:pt-4'>
        <div>
          <h1 className='text-[28px] font-bold leading-tight tracking-tight text-foreground'>Global Clients</h1>
          <p className='mt-1 text-[14px] text-[#6B7280]'>Clients attached to all locations.</p>
        </div>
        <button
          type='button'
          onClick={downloadCSV}
          className='inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#D1D5D8] rounded-lg text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
        >
          <Download className='h-4 w-4' />
          Export CSV
        </button>
      </AdasBox>

      {/* All / Active tabs — All omits the isActive filter, Active sends isActive=true */}
      <div className='mt-4 flex items-center gap-6 border-b border-[#EBEDF0]'>
        {[
          { value: false, label: 'All' },
          { value: true, label: 'Active' }
        ].map((t) => {
          const active = isActive === t.value
          return (
            <button
              key={String(t.value)}
              type='button'
              onClick={() => setIsActive(t.value)}
              className={`pb-3 inline-flex items-center gap-1.5 text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#334155]'
              }`}
            >
              {t.label}
              {active && totalCount > 0 && (
                <span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E8EFFF] px-1.5 text-[11px] font-bold text-[#1F6FEB]'>
                  {totalCount}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Filter row: center dropdown + search */}
      <AdasBox className='mt-5 flex flex-col sm:flex-row items-stretch gap-3'>
        <div className='relative w-full sm:w-[260px]'>
          <button
            type='button'
            onClick={() => setCenterDropdownOpen((o) => !o)}
            className='flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-[#D1D5D8] bg-white px-3 text-[13px] text-[#0A1628] hover:bg-[#F8FAFC]'
          >
            <span className='inline-flex items-center gap-2 truncate'>
              <Globe className='h-4 w-4 text-[#6B7280]' />
              <span className='truncate'>{selectedCenterLabel}</span>
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-[#6B7280] transition-transform ${centerDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {centerDropdownOpen && (
            <div className='absolute left-0 right-0 top-full z-30 mt-1 max-h-[320px] overflow-y-auto rounded-lg border border-[#EBEDF0] bg-white py-1 shadow-lg'>
              <button
                type='button'
                onClick={() => {
                  setCenterFilter(ALL_CENTERS)
                  setCenterDropdownOpen(false)
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-[#F8FAFC] ${
                  centerFilter === ALL_CENTERS ? 'font-semibold text-[#1F6FEB]' : 'text-[#0A1628]'
                }`}
              >
                <Globe className='h-4 w-4 text-[#6B7280]' />
                All calibration centers
              </button>
              {centers.map((cc) => (
                <button
                  key={cc.id}
                  type='button'
                  onClick={() => {
                    setCenterFilter(cc.id)
                    setCenterDropdownOpen(false)
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] hover:bg-[#F8FAFC] ${
                    centerFilter === cc.id ? 'font-semibold text-[#1F6FEB]' : 'text-[#0A1628]'
                  }`}
                >
                  <span className='truncate'>{cc.name}</span>
                  <span className='font-mono text-[11px] text-[#6B7280]'>{ccidFormatter(cc.id)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className='relative flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search clients, CCC ID, or center ID...'
            className='h-10 w-full rounded-lg border border-[#D1D5D8] bg-white pl-9 pr-3 text-[13px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none'
          />
        </div>
      </AdasBox>

      {/* Table */}
      <div className='mt-4 overflow-hidden rounded-card border border-border bg-card shadow-level-1'>
        <div className='overflow-x-auto'>
          <table className='w-full text-left text-[13px]'>
            <thead>
              <tr className='bg-gradient-to-r from-[#0A1628] to-[#1F2D3D] text-white'>
                {columns.map((col) => {
                  const isSorted = sortField === col.id
                  return (
                    <th
                      key={col.id}
                      onClick={col.sortable ? () => clickSortHandler(col.id) : undefined}
                      className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wide ${col.align === 'center' ? 'text-center' : 'text-left'} ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                    >
                      <span className='inline-flex items-center gap-1'>
                        {col.label}
                        {col.sortable &&
                          isSorted &&
                          (sort === 'ASC' ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' />)}
                      </span>
                    </th>
                  )
                })}
                {/* Edit (pencil) column at the end of the row. */}
                <th className='px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide'>
                  <span className='sr-only'>Edit</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className='px-4 py-10 text-center text-[#6B7280]'>
                    No clients found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={`${row.centerId}-${row.id}`}
                    className='border-t border-[#EBEDF0] transition-colors even:bg-[#F8FAFC] hover:bg-[#F1F4F8]'
                  >
                    <td className='px-4 py-3 text-[13px] font-medium text-[#0A1628]'>{row.name}</td>
                    <td className='px-4 py-3 font-mono text-[12px] text-[#475569]'>{row.token || '—'}</td>
                    <td className='px-4 py-3 text-[13px] text-[#0A1628]'>{row.centerName || '—'}</td>
                    <td className='px-4 py-3 font-mono text-[12px] text-[#475569]'>{row.centerCcid}</td>
                    <td className='px-4 py-3 text-center'>
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-3 py-0.5 text-[11px] font-semibold ${
                          row.is_active ? 'bg-[#DCFCE7] text-[#16803F]' : 'bg-[#EBEDF0] text-[#6B7280]'
                        }`}
                      >
                        {row.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {/* Edit (pencil) cell at the end of the row. */}
                    <td className='px-4 py-3 text-center'>
                      <button
                        type='button'
                        onClick={() => setEditClient(row)}
                        aria-label={`Edit client ${row.name}`}
                        className='inline-flex h-7 w-7 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBEDF0] hover:text-[#1F6FEB]'
                      >
                        <Pencil className='h-3.5 w-3.5' />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: count strip — Active/Inactive only count the current page; "Total"
          reflects the full filtered count returned by the API. */}
      <div className='mt-3 text-[12px] text-[#6B7280]'>
        {activeShown} active · {inactiveShown} inactive shown
        {totalCount > 0 && ` · ${totalCount} total`}
      </div>

      {editClient && (
        <ClientInfoModal
          open={!!editClient}
          clientInfo={editClient}
          closeDrawer={() => setEditClient(null)}
          onSaveSuccess={() => setRefreshTick((t) => t + 1)}
        />
      )}

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[10px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector pageSizeHandler={(e) => setPageSize(Number(e.target.value))} pageSize={pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination
              page={pageNo + 1}
              onChange={(_e, page) => {
                setPageNo(page - 1)
                scrollPageTop()
              }}
              count={totalPages || roundToGreatestInteger(totalCount / pageSize)}
              shape='rounded'
            />
          </AdasStack>
        </AdasBox>
      </AdasPaper>
    </div>
  )
}
